import {createSelector} from 'reselect'
import { State } from '../reducers/rootReducer'
import { E3DGrpAuthState } from '../reducers/e3DGrpAuthReducer'
import { getPersistedE3DGrpAuth } from '../persistLocalStorageE3DAuthState'
import is3DExhibitionEnabledSelector from './feature-scope/is3DExhibitionEnabledSelector'
import isE3dGrpDropdownEnabledSelector from './feature-scope/isE3dGrpDropdownEnabledSelector'
import countrySelector from './i18n/countrySelector'
import brandSelector from './brandSelector'
import { mapCountryCodeToISO3 } from '../../utils/countryCodeMapper'
import { mapBrandToE3D } from '../../utils/brandCodeMapper'

// Default state if nothing is available
const defaultState: E3DGrpAuthState = {
    user: null,
    shouldConnect: false,
    shouldConnectFromLoginComponent: false,
    loading: false,
    isLoggedIn: false,
    error: null,
    reinitCounter: 0,
    validDealerIds: [],
    selectedDealerId: null,
}

// Base selector that combines Redux state with localStorage if needed
export const grpAuthStateSelector = (state: State): E3DGrpAuthState => {
    const reduxState = state.e3DGrpAuth as E3DGrpAuthState | undefined

    if (reduxState === undefined) {
        const persistedState = getPersistedE3DGrpAuth()
        return persistedState || defaultState
    }

    return reduxState
}

// Derived selectors
export const isGrpLoggedInSelector = createSelector(
    grpAuthStateSelector,
    (grpAuthState): boolean => !!grpAuthState?.user,
)

export const isGrpLoginLoadingSelector = createSelector(
    grpAuthStateSelector,
    (grpAuthState): boolean => {
        return grpAuthState?.loading
    },
)

export const grpLoginStatusSelector = createSelector(
    [isGrpLoggedInSelector, isGrpLoginLoadingSelector],
    (isLoggedIn, isLoading): 'none' | 'loading' | 'success' | 'error' => {
        if (isLoading) {
            return 'loading'
        }
        if (isLoggedIn) {
            return 'success'
        }
        return 'none'
    },
)

export const validDealerIdsSelector = createSelector(
    [grpAuthStateSelector, countrySelector, brandSelector],
    (grpAuthState, country, brand): string[] => {
        if (!country) {
            return []
        }
        const market = mapCountryCodeToISO3(country)
        const brandCode = mapBrandToE3D(brand)
        return (grpAuthState?.validDealerIds ?? []).filter(
            (dealerId) => dealerId.slice(0, 3) === market && dealerId.slice(-1) === brandCode,
        )
    },
)

export const selectedDealerIdSelector = createSelector(
    [grpAuthStateSelector, validDealerIdsSelector],
    (grpAuthState, validDealerIds): string | null => {
        if (validDealerIds.length === 1) {
            return validDealerIds[0]
        }
        const selectedDealerId = grpAuthState?.selectedDealerId ?? null
        return selectedDealerId && validDealerIds.includes(selectedDealerId) ? selectedDealerId : null
    },
)

export const isDealerIdSelectionRequiredSelector = createSelector(
    [is3DExhibitionEnabledSelector, isE3dGrpDropdownEnabledSelector, isGrpLoggedInSelector, validDealerIdsSelector, selectedDealerIdSelector],
    (is3DExhibitionEnabled, isGrpDropdownEnabled, isLoggedIn, validDealerIds, selectedDealerId): boolean =>
        is3DExhibitionEnabled && isGrpDropdownEnabled && isLoggedIn && validDealerIds.length > 1 && !selectedDealerId,
)

// Without the e3dGrpDropdown scope E3D starts right away, as before the dealer ID dropdown existed.
export const isE3DDealerIdReadySelector = createSelector(
    [isE3dGrpDropdownEnabledSelector, selectedDealerIdSelector],
    (isGrpDropdownEnabled, selectedDealerId): boolean => !isGrpDropdownEnabled || !!selectedDealerId,
)
